import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Slide,
  Fade,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  alpha,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AnimatedBackground from './AnimatedBackground';
import QualificationForm from './QualificationForm';
import PricingSection from './PricingSection';
import axios from 'axios';
const LeadPlans = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [animate, setAnimate] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [recommendedPlan, setRecommendedPlan] = useState(null);
  
  // Initialize form data from localStorage or use default values
  const [formData, setFormData] = useState(() => {
    const savedFormData = localStorage.getItem('leadPlansFormData');
    return  {
      insuranceType: "",
      experience: "",
      transfers: "",
      weeklyLeads: "",
      apGoal: "",
      crm: "",
      method: "",
      ready: "",
      terms: false,
    };
  });

  // Form validation state
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  
  // Save form data to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('leadPlansFormData', JSON.stringify(formData));
  }, [formData]);

  // Handle form changes
  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Clear error when field is changed
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }

    // Mark field as touched
    if (!touched[name]) {
      setTouched(prev => ({
        ...prev,
        [name]: true
      }));
    }

    // Automatically recommend a plan based on form data
    if (["weeklyLeads", "apGoal", "experience"].includes(name)) {
      const newFormData = {
        ...formData,
        [name]: newValue
      };
      
      // Simple logic to recommend a plan
      if (newFormData.weeklyLeads === "100" || newFormData.apGoal === "30-50k") {
        setRecommendedPlan("enterprise");
      } else if (newFormData.weeklyLeads === "60" || newFormData.apGoal === "20-30k") {
        setRecommendedPlan("pro");
      } else {
        setRecommendedPlan("starter");
      }
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    const requiredFields = [
      "insuranceType",
      "experience",
      "transfers",
      "weeklyLeads",
      "apGoal",
      "crm",
      "method",
      "ready"
    ];

    requiredFields.forEach(field => {
      if (!formData[field]) {
        newErrors[field] = "This field is required";
      }
    });

    if (!formData.terms) {
      newErrors.terms = "You must accept the terms";
    }

    setErrors(newErrors);
    
    // Mark all fields as touched when form is submitted
    const allTouched = {};
    [...requiredFields, "terms"].forEach(field => {
      allTouched[field] = true;
    });
    setTouched(allTouched);
    
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission to move to pricing step
  const handleQualificationSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Scroll to first error
      const firstError = Object.keys(errors)[0];
      const errorElement = document.querySelector(`[name="${firstError}"]`);
      errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Log form data to console
      console.log("Form Data:", formData);
      
      // Check if user is not ready
      if (formData.ready === "no") {
        alert("Our services are for agents ready to scale. Please return when prepared.");
        navigate("/exit-page");
        return;
      }
      
      // Move to pricing step
      setActiveStep(1);
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle plan selection
  const handlePlanSelect = async (plan) => {
    setLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Log selected plan along with form data
      console.log("Selected Plan:", plan, "Form Data:", formData);
      
      // Save selected plan to localStorage
      localStorage.setItem('selectedPlan', plan);
      
      const userFormData=  localStorage.getItem('userFormData');
      console.log("User Form Data:", userFormData);
const finalData= {

  plan: plan,
  ...JSON.parse(userFormData),
  ...formData,
}
 await axios.post("https://kalisunmarketing.com/api/zapier.php", finalData);
const response= await axios.post('https://kalisunmarketing.com/api/index.php', finalData);
console.log("Response:", response);

      navigate("/bookings");
      // Show success message
      // alert("Thank you for selecting a plan! Our team will contact you shortly.");
      
      // In a real app, you would save this data and navigate to a checkout page
    } catch (error) {
      console.error("Error selecting plan:", error);
    } finally {
      setLoading(false);
    }
  };
  
  // Go back to qualification form
  const handleBack = () => {
    setActiveStep(0);
  };
  
  // Animation on mount
  useEffect(() => {
    setAnimate(true);
  }, []);

  // Helper function to determine if a field has an error
  const hasError = (field) => {
    return touched[field] && errors[field];
  };

  // Helper function to get error message
  const getErrorMessage = (field) => {
    return hasError(field) ? errors[field] : "";
  };

  return (
    <Box sx={{ minHeight: "100vh", position: "relative", pt: { xs: 10, md: 12 }, pb: 10 }}>
      <AnimatedBackground />
      
      <Container maxWidth="lg">
      <Slide in={animate} direction="down" timeout={800}>
      <Box
        sx={{
          textAlign: "center",
          mb: 8,
          display: {
            xs: "none",
            sm: "block"
          }
        }}
      >
        <Typography
          variant="h2"
          gutterBottom
          sx={{
            fontWeight: 800,
            mb: 3,
            background: "linear-gradient(45deg, #fff 30%, #64B5F6 90%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            textShadow: "0 5px 30px rgba(100, 181, 246, 0.3)"
          }}
        >
          Get High-Intent, Exclusive Life Insurance Leads
        </Typography>

        <Typography
          variant="h6"
          sx={{
            color: alpha(theme.palette.text.secondary, 0.9),
            maxWidth: 800,
            mx: "auto",
            lineHeight: 1.6
          }}
        >
          TCPA-Compliant • 100% Exclusive • CRM-Ready
        </Typography>
      </Box>
    </Slide>

        
        {/* Stepper */}
        <Box sx={{ mb: 6 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            <Step>
              <StepLabel>
                <Typography sx={{ color: activeStep === 0 ? '#64B5F6' : 'text.secondary' }}>
                  Agent Qualification
                </Typography>
              </StepLabel>
            </Step>
            <Step>
              <StepLabel>
                <Typography sx={{ color: activeStep === 1 ? '#64B5F6' : 'text.secondary' }}>
                  Select Your Plan
                </Typography>
              </StepLabel>
            </Step>
          </Stepper>
        </Box>

        {/* Qualification Form Section - Shown First */}
        <Fade in={animate && activeStep === 0} timeout={800}>
          <Box sx={{ display: activeStep === 0 ? 'block' : 'none' }}>
            <QualificationForm
              formData={formData}
              errors={errors}
              touched={touched}
              loading={loading}
              handleChange={handleChange}
              handleQualificationSubmit={handleQualificationSubmit}
              hasError={hasError}
              getErrorMessage={getErrorMessage}
            />
          </Box>
        </Fade>

        {/* Pricing Section - Shown Second */}
        <Fade in={animate && activeStep === 1} timeout={800}>
          <Box sx={{ display: activeStep === 1 ? 'block' : 'none' }}>
            <PricingSection
              loading={loading}
              recommendedPlan={recommendedPlan}
              handlePlanSelect={handlePlanSelect}
              handleBack={handleBack}
            />
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default LeadPlans;